import { useState } from 'react';
import {
  createFrontendPlugin,
  PageBlueprint,
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/frontend-plugin-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  InfoCard,
  WarningPanel,
  Header,
  Page,
  Content,
} from '@backstage/core-components';
import {
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Grid,
  Snackbar,
  CircularProgress,
  Link,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import DnsIcon from '@material-ui/icons/Dns';
import { dnsRecordPermission } from './conduitPermissions';

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'TXT', 'MX', 'NS', 'SRV', 'PTR', 'CAA'];

function DnsRecordForm() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [form, setForm] = useState({
    zone_name: '',
    zone_is_private: false,
    hostname: '',
    record_type: 'A',
    values: '',
    ttl: 300,
    allow_overwrite: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ runId: string; runUrl: string; status: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = (field: string, value: string | number | boolean) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('conduit-dns-record');
      const response = await fetchApi.fetch(`${baseUrl}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || `Request failed (${response.status})`);
      } else {
        setResult(data);
      }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header title="DNS Record" subtitle="Create and manage Route 53 DNS records via Terraform Cloud" />
      <Content>
        <RequirePermission
          permission={dnsRecordPermission}
          errorPage={
            <WarningPanel title="Access Denied" severity="error">
              This function requires the <strong>Platform Engineer</strong> or{' '}
              <strong>Platform Admin</strong> role.
            </WarningPanel>
          }
        >
          <InfoCard title="DNS Record Parameters">
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Zone Name"
                    helperText="Existing Route 53 hosted zone (e.g., younglife.org)"
                    value={form.zone_name}
                    onChange={e => set('zone_name', e.target.value)}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Hostname"
                    helperText="Record name, relative to zone or fully qualified"
                    value={form.hostname}
                    onChange={e => set('hostname', e.target.value)}
                    required
                    fullWidth
                    placeholder="app.younglife.org"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Record Type"
                    select
                    value={form.record_type}
                    onChange={e => set('record_type', e.target.value)}
                    required
                    fullWidth
                  >
                    {RECORD_TYPES.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="TTL (seconds)"
                    helperText="Time-to-live, 0–86400"
                    type="number"
                    value={form.ttl}
                    onChange={e => set('ttl', Number(e.target.value))}
                    fullWidth
                    inputProps={{ min: 0, max: 86400 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <div style={{ paddingTop: 8 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.zone_is_private}
                          onChange={e => set('zone_is_private', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Private zone"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.allow_overwrite}
                          onChange={e => set('allow_overwrite', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Overwrite existing"
                    />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Values"
                    helperText="One value per line. For A records: IP addresses. For CNAME: target hostname. For MX: priority and target."
                    value={form.values}
                    onChange={e => set('values', e.target.value)}
                    required
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder={"192.168.1.1\n192.168.1.2"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} /> : undefined}
                  >
                    {submitting ? 'Creating...' : 'Create DNS Record'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </InfoCard>
          {result && (
            <Alert severity="success" style={{ marginTop: 16 }}>
              Terraform Cloud run queued.{' '}
              <Link href={result.runUrl} target="_blank" rel="noopener">
                View run {result.runId}
              </Link>{' '}
              — status: {result.status}
            </Alert>
          )}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
          >
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Snackbar>
        </RequirePermission>
      </Content>
    </Page>
  );
}

export default createFrontendPlugin({
  pluginId: 'conduit-dns-record',
  extensions: [
    PageBlueprint.make({
      params: {
        path: '/dns-record',
        title: 'DNS Record',
        icon: <DnsIcon />,
        loader: async () => <DnsRecordForm />,
      },
    }),
  ],
});
